import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useWorkflow } from '../context/WorkflowContext';
import { Globe, FileText, Sparkles, Users, Wifi, WifiOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { workflowAPI } from '../services/api';

const WorkflowContainer = styled.div`
  width: 100vw;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
  }
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.3) 0%, transparent 50%);
  z-index: 0;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 800px;
  width: 100%;
`;

const Title = styled(motion.h1)`
  color: white;
  font-size: 3.5rem;
  font-weight: 300;
  margin-bottom: 1rem;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 1024px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled(motion.p)`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.3rem;
  margin-bottom: 3rem;
  line-height: 1.6;
  
  @media (max-width: 1024px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
`;

const InputSection = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 10px;
    margin-bottom: 1rem;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: white;
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 0.8rem;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.7rem;
    font-size: 0.85rem;
  }
`;

const Button = styled(motion.button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.8rem 1.5rem;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.7rem 1.2rem;
    font-size: 0.9rem;
  }
`;

const FeatureList = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.8rem;
    margin-top: 1.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
    margin-top: 1rem;
  }
`;

const Feature = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 1.2rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 10px;
  }
`;

const FeatureIcon = styled.div`
  color: white;
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.h3`
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const FeatureDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const WorkflowManager = () => {
  const navigate = useNavigate();
  const { startWorkflow, isProcessing } = useWorkflow();
  const [inputData, setInputData] = useState({
    directory_path: '',
    website_url: ''
  });
  const [serverStatus, setServerStatus] = useState('checking');

  // Check server status on component mount
  useEffect(() => {
    const checkServerStatus = async () => {
      try {
        await workflowAPI.healthCheck();
        setServerStatus('connected');
      } catch (error) {
        console.error('Server health check failed:', error);
        setServerStatus('disconnected');
      }
    };

    checkServerStatus();
    // Check every 10 seconds
    const interval = setInterval(checkServerStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (field, value) => {
    setInputData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStartWorkflow = async () => {
    if (!inputData.directory_path && !inputData.website_url) {
      toast.error('Please provide either a directory path or website URL');
      return;
    }

    try {
      toast.loading('Starting workflow... This may take a few minutes for document processing.', { 
        id: 'workflow-start',
        duration: 120000 // Show loading for up to 2 minutes
      });
      
      const response = await startWorkflow(inputData);
      
      toast.success('Workflow started successfully!', { id: 'workflow-start' });
      navigate('/story');
    } catch (error) {
      console.error('Failed to start workflow:', error);
      toast.error(`Failed to start workflow: ${error.message}`, { id: 'workflow-start' });
      
      // Show more detailed error information
      if (error.message.includes('ECONNREFUSED')) {
        toast.error('Cannot connect to FastAPI server. Please make sure it\'s running on port 8001.', { duration: 6000 });
      } else if (error.message.includes('Network Error')) {
        toast.error('Network error. Please check your connection and try again.', { duration: 6000 });
      } else if (error.message.includes('timeout')) {
        toast.error('Request timed out. Document processing is taking longer than expected. Please try with a smaller directory or check the server logs.', { duration: 8000 });
      } else if (error.message.includes('500')) {
        toast.error('Server error occurred during document processing. Please check the server logs and try again.', { duration: 6000 });
      }
    }
  };

  return (
    <WorkflowContainer>
      <BackgroundPattern />
      <Content>
        <Title
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Interactive Storytelling Game
        </Title>
        
        <Subtitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Transform your educational content into an engaging 3D storytelling experience
        </Subtitle>

        {/* Server Status Indicator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '2rem',
            padding: '0.5rem 1rem',
            background: serverStatus === 'connected' ? 'rgba(74, 222, 128, 0.2)' : 'rgba(239, 68, 68, 0.2)',
            border: `1px solid ${serverStatus === 'connected' ? '#4ADE80' : '#EF4444'}`,
            borderRadius: '20px',
            color: 'white',
            fontSize: '0.9rem'
          }}
        >
          {serverStatus === 'connected' ? <Wifi size={16} /> : <WifiOff size={16} />}
          {serverStatus === 'connected' ? 'FastAPI Server Connected' : 
           serverStatus === 'disconnected' ? 'FastAPI Server Disconnected' : 
           'Checking Server Status...'}
        </motion.div>

        <InputSection
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >

          <InputGroup>
            <Label>
              <Globe size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Website URL
            </Label>
            <Input
              type="url"
              placeholder="Enter website URL to analyze..."
              value={inputData.website_url}
              onChange={(e) => handleInputChange('website_url', e.target.value)}
            />
          </InputGroup>

          <Button
            onClick={handleStartWorkflow}
            disabled={isProcessing}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Sparkles size={20} />
            {isProcessing ? 'Starting...' : 'Start Storytelling Journey'}
          </Button>
        </InputSection>

        <FeatureList
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <Feature
            whileHover={{ scale: 1.05, y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <FeatureIcon>
              <FileText size={32} />
            </FeatureIcon>
            <FeatureTitle>Smart Analysis</FeatureTitle>
            <FeatureDescription>
              AI-powered content analysis and story generation
            </FeatureDescription>
          </Feature>

          <Feature
            whileHover={{ scale: 1.05, y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <FeatureIcon>
              <Users size={32} />
            </FeatureIcon>
            <FeatureTitle>Interactive Dialogues</FeatureTitle>
            <FeatureDescription>
              3D teacher-student conversations with realistic characters
            </FeatureDescription>
          </Feature>

          <Feature
            whileHover={{ scale: 1.05, y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <FeatureIcon>
              <Sparkles size={32} />
            </FeatureIcon>
            <FeatureTitle>Immersive Experience</FeatureTitle>
            <FeatureDescription>
              Engaging 3D environment with smooth animations
            </FeatureDescription>
          </Feature>
        </FeatureList>
      </Content>
    </WorkflowContainer>
  );
};

export default WorkflowManager;


